import { supabase } from "@/integrations/supabase/client";
import type { ValuationRun, ValuationSuburbMonthlyResult } from "./valuations";

// Source-selection mode persisted on `valuation_runs.filters.sources`.
// Mirrors the union accepted by the edge function.
export type ValuationSourceMode = "all" | "internal_only" | "sqm_only" | "adapter";

export interface ValuationSourceSelection {
  mode: ValuationSourceMode;
  adapterSlug?: string | null;
}

export type ValuationRunStepStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "skipped";

export interface ValuationRunStep {
  id: string;
  run_id: string;
  step_order: number;
  step_slug: string;
  step_label: string;
  status: ValuationRunStepStatus;
  parameters: Record<string, unknown>;
  result_summary: Record<string, unknown>;
  sample_row_refs: Array<{ table: string; id: string }>;
  input_count: number | null;
  output_count: number | null;
  warnings: unknown[];
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  created_at: string;
}

export interface ValuationRunSourceSnapshot {
  id: string;
  run_id: string;
  source_id: string;
  snapshot_kind: string;
  selection: Record<string, unknown>;
  record_count: number;
  summary: Record<string, unknown>;
  created_at: string;
  valuation_data_sources?: { slug: string; name: string } | null;
}

export interface ValuationRunDetail {
  run: ValuationRun;
  steps: ValuationRunStep[];
  sources: ValuationRunSourceSnapshot[];
}

export interface ValuationTraceMonth {
  saleMonth: string;
  rawPriceCount: number;
  outliersRemoved: number;
  samplePriceCount: number;
  minPrice: number | null;
  maxPrice: number | null;
  averagePrice: number | null;
  medianPrice: number | null;
  formula: string;
  formulaInputs: { n: number; sum: number };
}

export interface ValuationTraceSampleRow {
  saleDate: string | Date;
  salePrice: number;
  propertyType: string | null;
  saleEventKey: string | null;
}

export interface ValuationTraceResult {
  runId: string;
  saleMonth: string;
  output: ValuationSuburbMonthlyResult | null;
  trace: ValuationTraceMonth | null;
  counts: {
    totalInWindow: number;
    duplicatesDroppedAcrossRun: number;
    monthTotal: number;
    monthSampleReturned: number;
  };
  contribution: {
    internal_property_snapshots: number;
    sqm_back_history: number;
  };
  sampleRows: ValuationTraceSampleRow[];
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("run-valuation-algorithm", { body });
  if (error) {
    throw error;
  }
  const envelope = data as { success?: boolean; error?: string };
  if (envelope?.success === false) {
    throw new Error(envelope.error ?? "Valuation action failed");
  }
  return data as T;
}

export interface RunMonthlyMovementInput {
  suburbId: string;
  months?: number;
  bedrooms?: number | null;
  bathrooms?: number | null;
  parking?: number | null;
  sources?: ValuationSourceSelection;
}

export async function runMonthlyMovement(input: RunMonthlyMovementInput): Promise<{
  runId: string;
  results: ValuationSuburbMonthlyResult[];
  summary: Record<string, unknown>;
}> {
  return invoke({
    action: "run-monthly",
    suburbId: input.suburbId,
    months: input.months ?? 12,
    bedrooms: input.bedrooms ?? null,
    bathrooms: input.bathrooms ?? null,
    parking: input.parking ?? null,
    sources: input.sources ?? { mode: "all" },
  });
}

export async function replayValuationRun(input: {
  runId: string;
  overrides?: Partial<RunMonthlyMovementInput>;
}): Promise<{
  runId: string;
  results: ValuationSuburbMonthlyResult[];
  summary: Record<string, unknown>;
}> {
  return invoke({
    action: "replay-run",
    runId: input.runId,
    overrides: input.overrides ?? {},
  });
}

export async function deleteValuationRun(runId: string): Promise<{ runId: string }> {
  return invoke({ action: "delete-run", runId });
}

export async function fetchValuationRunDetail(runId: string): Promise<ValuationRunDetail> {
  const result = await invoke<{
    success: boolean;
    run: ValuationRun;
    steps: ValuationRunStep[];
    sources: ValuationRunSourceSnapshot[];
  }>({ action: "get-run-detail", runId });
  return { run: result.run, steps: result.steps, sources: result.sources };
}

export async function traceValuationResult(input: {
  runId: string;
  saleMonth: string;
  sampleLimit?: number;
}): Promise<ValuationTraceResult> {
  const result = await invoke<{ success: boolean } & ValuationTraceResult>({
    action: "trace-result",
    runId: input.runId,
    saleMonth: input.saleMonth,
    sampleLimit: input.sampleLimit ?? 25,
  });
  return result;
}

/**
 * Helper for the run-history "select run" flow: pulls source-selection out
 * of the run's `filters` JSONB with a safe default so the UI doesn't have
 * to defend against missing values.
 */
export function readSourceSelectionFromRun(run: ValuationRun | null): ValuationSourceSelection {
  if (!run) {
    return { mode: "all" };
  }
  const raw = (run.filters as { sources?: unknown })?.sources;
  if (!raw || typeof raw !== "object") {
    return { mode: "all" };
  }
  const mode = (raw as { mode?: unknown }).mode;
  const adapterSlug = (raw as { adapterSlug?: unknown }).adapterSlug;
  if (mode === "internal_only" || mode === "sqm_only") {
    return { mode };
  }
  if (mode === "adapter" && typeof adapterSlug === "string") {
    return { mode: "adapter", adapterSlug };
  }
  return { mode: "all" };
}

// Re-export type used by the helper.
export type { ValuationRun, ValuationSuburbMonthlyResult };
